import clsx from "clsx";
import type { ReactNode } from "react";

export interface FieldProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  /** Inline validation message — also wires `aria-describedby`. */
  error?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint/error, with the aria wiring done once. */
export function Field({ id, label, hint, error, required, className, children }: FieldProps) {
  return (
    <div className={clsx("flex flex-col gap-1", className)}>
      {label && (
        <span className="flex items-baseline gap-0.5 text-sm font-medium text-ink-700 dark:text-ink-200">
          {/* The asterisk lives outside <label> so the accessible name stays the
              plain label text — `required` already conveys it to assistive tech. */}
          <label htmlFor={id}>{label}</label>
          {required && <span aria-hidden="true">*</span>}
        </span>
      )}
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-ink-500 dark:text-ink-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}

export const controlClasses =
  "w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 " +
  "focus:border-accent-600 disabled:cursor-not-allowed disabled:bg-ink-100 " +
  "dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50 dark:placeholder:text-ink-500 dark:disabled:bg-ink-800";

export function describedBy(id: string, hint?: ReactNode, error?: string): string | undefined {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}
